import {
  CSRF_HEADER,
  type FolderId,
  type ProjectDetail,
  type PublishWikiChangeSetRequest,
  type WikiChangeSet,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { AppDialog } from "@/components/AppDialog";
import { Notice } from "@/components/Notice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { api } from "../api.ts";
import { ok } from "../api-errors.ts";
import { canChange } from "../projects/capabilities.ts";
import { FolderField } from "../projects/FolderPicker.tsx";
import { useSession } from "../session.tsx";
import { AudienceLine } from "../trust/AudienceLine.tsx";
import { peopleAudience } from "../trust/audience.ts";
import { ItemWarnings } from "./ItemWarnings.tsx";
import { wikiKeys } from "./queries.ts";

export function ChangeSetPublishDialog(props: {
  open: boolean;
  onClose: () => void;
  project: ProjectDetail;
  changeSet: WikiChangeSet;
}) {
  const { t } = useTranslation();
  return (
    <AppDialog
      open={props.open}
      onClose={props.onClose}
      title={t("wiki.publish.titleOf", {
        title: props.changeSet.title || t("wiki.changes.hiddenTitle"),
      })}
      description={t("wiki.changes.publishExplain")}
    >
      <PublishChangeSet
        project={props.project}
        changeSet={props.changeSet}
        onClose={props.onClose}
      />
    </AppDialog>
  );
}
function PublishChangeSet({
  project,
  changeSet,
  onClose,
}: {
  project: ProjectDetail;
  changeSet: WikiChangeSet;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { csrfToken, userId } = useSession();
  const queryClient = useQueryClient();
  const [includePending, setIncludePending] = useState(false);
  const [decisions, setDecisions] = useState<
    Record<string, { folderId?: FolderId | null; widen?: boolean; reason?: string }>
  >({});
  const [done, setDone] = useState(false);
  const endpoint = api.api.projects[":projectId"].wiki["change-sets"][":id"];
  const param = { projectId: project.id, id: changeSet.id };
  const headers = { [CSRF_HEADER]: csrfToken };
  const preview = useQuery({
    queryKey: [...wikiKeys.all(project.id), "preview", changeSet.id, includePending],
    gcTime: 0,
    retry: false,
    queryFn: async () =>
      ok(await endpoint.preview.$post({ param, json: { includePending } }, { headers })),
  });
  const chosen = changeSet.items.filter(
    (i) => i.status === "approved" || (includePending && i.status === "pending"),
  );
  const update = (id: string, value: (typeof decisions)[string]) =>
    setDecisions((was) => ({ ...was, [id]: { ...was[id], ...value } }));
  const request = (): PublishWikiChangeSetRequest => ({
    includePending,
    items: chosen.map((item) => {
      const decision = decisions[item.id];
      return {
        id: item.id,
        ...(item.op === "create"
          ? {
              folderId:
                decision?.folderId === undefined
                  ? (item.folderId as FolderId | null)
                  : decision.folderId,
            }
          : {}),
        ...(decision?.widen ? { declassify: { reason: decision.reason?.trim() ?? "" } } : {}),
      };
    }),
  });
  const publish = useMutation({
    mutationFn: async () =>
      ok(await endpoint.publish.$post({ param, json: request() }, { headers })),
    onSuccess: () => {
      setDone(true);
      void queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: wikiKeys.all(project.id) });
    },
  });
  if (done) {
    return (
      <div className="flex flex-col gap-4">
        <Notice tone="success">{t("wiki.publish.published")}</Notice>
        <DialogFooter>
          <Button onClick={onClose}>{t("wiki.publish.done")}</Button>
        </DialogFooter>
      </div>
    );
  }
  const valid =
    chosen.length > 0 &&
    preview.data &&
    chosen.every((item) => {
      const p = preview.data?.items.find((i) => i.id === item.id);
      return p && (!decisions[item.id]?.widen || !!decisions[item.id]?.reason?.trim());
    });
  return (
    <form
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        if (valid) {
          publish.mutate();
        }
      }}
    >
      <div className="flex items-center gap-2">
        <Checkbox
          id="include-pending-pages"
          checked={includePending}
          onCheckedChange={(on) => setIncludePending(on === true)}
        />
        <Label htmlFor="include-pending-pages">{t("wiki.changes.includePending")}</Label>
      </div>
      {preview.isPending ? (
        <Skeleton className="h-32" />
      ) : preview.isError ? (
        <Notice tone="danger">{preview.error.message}</Notice>
      ) : (
        <>
          {!chosen.length ? <Notice>{t("wiki.changes.approveFirst")}</Notice> : null}
          <ul className="divide-y divide-line">
            {chosen.map((item) => {
              const p = preview.data.items.find((i) => i.id === item.id)?.preview;
              const decision = decisions[item.id];
              const declassification = p?.declassification;
              return (
                <li key={item.id} className="flex flex-col gap-3 py-5 first:pt-0">
                  <h3 className="text-sm font-semibold text-fg">{item.title}</h3>
                  <p className="text-xs text-fg-muted">{t(`wiki.changes.op.${item.op}`)}</p>
                  <ItemWarnings warnings={item.warnings} compact />
                  {p ? (
                    <>
                      <AudienceLine
                        restricted
                        text={peopleAudience(
                          p.readers.length,
                          p.readers.length === 1 && p.readers[0]?.userId === userId,
                        )}
                      />
                      <p className="text-xs text-fg-secondary">
                        {t("wiki.publish.readersExplain")}
                      </p>
                      <ul className="text-sm text-fg-secondary">
                        {p.readers.map((reader) => (
                          <li key={reader.userId}>{reader.displayName ?? reader.userId}</li>
                        ))}
                      </ul>
                    </>
                  ) : (
                    <p className="text-sm text-fg-secondary">
                      {t("wiki.changes.audienceUnchanged")}
                    </p>
                  )}
                  {item.sources.length ? (
                    <ul className="text-xs text-fg-secondary">
                      {item.sources.map((source) => (
                        <li key={source.id}>{source.title ?? t("wiki.draft.sourceDeleted")}</li>
                      ))}
                    </ul>
                  ) : null}
                  {item.op === "create" ? (
                    <FolderField
                      projectId={project.id}
                      section="wiki"
                      value={
                        decision?.folderId === undefined
                          ? (item.folderId as FolderId | null)
                          : decision.folderId
                      }
                      onChange={(folderId) => update(item.id, { folderId })}
                      canCreate={canChange(project, "folder.manage")}
                    />
                  ) : null}
                  {declassification?.available && canChange(project, "declassify") ? (
                    <section className="flex flex-col gap-3 rounded-md border border-warning-border bg-warning-bg p-3">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id={`widen-${item.id}`}
                          checked={decision?.widen ?? false}
                          onCheckedChange={(on) => update(item.id, { widen: on === true })}
                        />
                        <Label htmlFor={`widen-${item.id}`}>{t("wiki.publish.widen")}</Label>
                      </div>
                      <ul className="text-xs text-fg-secondary">
                        {declassification.sources.map((source) => (
                          <li key={source.id}>
                            {source.title} ·{" "}
                            {t("wiki.publish.cannotRead", { count: source.cannotRead })}
                          </li>
                        ))}
                      </ul>
                      {decision?.widen ? (
                        <>
                          <p className="text-sm text-fg">
                            {t("wiki.publish.widenExplain", { count: p?.intendedReaderCount })}
                          </p>
                          <Label htmlFor={`reason-${item.id}`}>{t("wiki.publish.reason")}</Label>
                          <Textarea
                            id={`reason-${item.id}`}
                            required
                            maxLength={2000}
                            value={decision.reason ?? ""}
                            onChange={(e) => update(item.id, { reason: e.currentTarget.value })}
                          />
                          <p className="text-sm text-warning-fg">
                            {t("wiki.publish.irreversible")}
                          </p>
                        </>
                      ) : null}
                    </section>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </>
      )}
      {publish.isError ? <Notice tone="danger">{publish.error.message}</Notice> : null}
      <DialogFooter sticky>
        <Button type="button" variant="outline" onClick={onClose}>
          {t("wiki.publish.cancel")}
        </Button>
        <Button type="submit" disabled={!valid || publish.isPending}>
          {t(publish.isPending ? "wiki.publish.publishing" : "wiki.publish.submit")}
        </Button>
      </DialogFooter>
    </form>
  );
}
